
import { request, setLoadingId } from "./pip3d_common";

const dbg = console.log;
const warn = console.warn;
const err = console.error;

const events = [ 'mousedown', 'touchend', 'keydown', 'gamepadconnected' ];

if (!window['RTCPeerConnection'])
    window['RTCPeerConnection'] = window['webkitRTCPeerConnection'];

let currentCtx;
function autoUnmute(settings) {
    const tryUnmute = e => {
        const audio = document.getElementById('audio');
        if (audio && (audio.muted || audio.paused)) {
            const playSuccess = () => {
                if (currentCtx.playTime)
                    audio.currentTime = (Date.now() - currentCtx.playTime) / 1000;
                if (typeof settings.unmuteCb === 'function') settings.unmuteCb();
                for (const event of events)
                    window.removeEventListener(event, tryUnmute);
            };
            const playError = (error) => {
                if (e)
                    warn("audio: failed to unmute & play, was it a real interaction?");
                audio.muted = true;
            };
            try {
                audio.muted = false;
                // needed as autoplay seems broken on chrome
                const p = audio.play();
                // some browser do not return a promise
                if (p && p.then)
                    p.then(playSuccess).catch(playError);
                else
                    playSuccess();
            } catch(error) {
                playError(error);
            }
        }
    };
    const autoUnmuteElement = (settings.autoUnmuteElement != null) ? settings.autoUnmuteElement : window;
    for (const event of events) {
        autoUnmuteElement.addEventListener(event, tryUnmute);
    }
    tryUnmute();
}

export function getRTCPeerConnectionConf(ctx) {
    const iceServers = ctx.settings.iceServers;
    const controllerUrl = ctx.settings.signalingUrl;
    let conf = {
        sdpSemantics: 'unified-plan', // needed on tizen
    };

    let promises = [];
    let xhrs = [];
    if (iceServers && iceServers.length > 0) {
        conf.iceServers = [];
        for (const url of iceServers.split(',')) {
            if (url.length == 0) continue;
            let iceServer = { urls: url };
            if (url.startsWith("turn:")) {
                if (!controllerUrl) {
                    err("audio: cannot get TURN credentials");
                    continue;
                }
                const url = new URL(controllerUrl);
                const credentialsUrl = url.protocol + '//' + url.host + url.pathname + '/credentials' + url.search;
                const [ xhr, req ] = request('GET', credentialsUrl, undefined, undefined, ctx.settings.connectTimeout);
                promises.push(req.then(res => {
                    const data = JSON.parse(res);
                    iceServer.username = data.turnLogin.username;
                    iceServer.credential = data.turnLogin.credential;
                    conf.iceServers.push(iceServer);
                }).catch((error) => {
                    err("audio: failed to add ice server", url, error);
                }));
                xhrs.push(xhr);
            } else
                conf.iceServers.push(iceServer);
        }
    }

    return new Promise((resolve, reject) => {
        ctx.abort = () => {
            for (const xhr of xhrs)
                xhr.abort();
            reject("abort");
        };

        Promise.all(promises).then(() => {
            ctx.abort = undefined;
            if (conf.iceServers.length == 0)
                throw "no available ice server";
            resolve(conf);
        }).catch(reject);
    });
}

export function start2(settings) {
    const audio = document.getElementById('audio');
    if (!audio) return;

    if (currentCtx) _stop(currentCtx);

    const ctx = {
        settings: settings,
    };
    currentCtx = ctx;

    return new Promise((resolve, reject) => {
        if (settings.staticUrl) {
            audio.src = settings.staticUrl;
            addEventListener("n2i_pip3d_frame", function() {
                seek(0);
                autoUnmute(settings);
            }, { once: true });
            audio.addEventListener("loadedmetadata", function() {
                resolve();
            }, { once: true });
            audio.addEventListener("error", function() {
                reject();
            }, { once: true });
            return;
        }
        const tryConnect = () => {
            const getWebRTCConf = settings.getWebRTCConf || function() {
                return getRTCPeerConnectionConf(ctx);
            };
            if (ctx.stopping) {
                reject(new Error('audio: try to connect while WebRTC is stopping'));
                return;
            }
            dbg("audio: starting");
            // get a new conf at each connection tentative to get new TURN credentials
            return getWebRTCConf().then(conf => {
                dbg("audio: WebRTC conf", conf);
                ctx.pc = new RTCPeerConnection(conf);
                return connectSignalingChannel(ctx).then(() => {
                    if (!settings.disableRetry) {
                        // retryConnect is needed for closed after signaling channel connected
                        ctx.retryConnect = () => {
                            return _stop(ctx).then(tryConnect);
                        };
                    }
                    return connectWebRtc(ctx).then(() => {
                        dbg("audio: connected");
                        resolve(ctx);
                    });
                });
            }).catch(function(error) {
                err("audio: error", error);
                if (!settings.disableRetry) {
                    setTimeout(tryConnect, 1000);
                    setTimeout(() => { throw error });
                } else
                    reject(error);
            });
        };
        return tryConnect();
    });
}

function connectSignalingChannel(ctx) {
    const pc = ctx.pc;
    const signalingUrl = setLoadingId(ctx.settings.signalingUrl.replace("http", "ws"));
    dbg("audio: connecting signaling channel");
    const ws = new WebSocket(signalingUrl);
    ws.onmessage = event => {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
            case "sdp":
                const answer = JSON.parse(msg.data);
                dbg("WebRTC answer", answer);
                pc.setRemoteDescription(new RTCSessionDescription(answer));
                break;
            case "candidate":
                const candidate = JSON.parse(msg.data);
                dbg("audio: WebRTC remote candidate", candidate);
                pc.addIceCandidate(new RTCIceCandidate(candidate));
                break;
        }
    };
    ctx.ws = ws;
    return new Promise((resolve, reject) => {
        let timeout;
        if (ctx.settings.connectTimeout)
            timeout = setTimeout(() => {
                reject("timeout");
                ws.close();
            }, ctx.settings.connectTimeout);
        ws.onopen = () => {
            clearTimeout(timeout);
            dbg("audio: signaling channel opened");
            resolve();
        };
        ws.onclose = e => {
            dbg(`audio: signaling channel closed code ${e.code} reason ${e.reason}`);
            ctx.ws = null;
            if (e.reason != "shutdown" && ctx.retryConnect) {
                err(`audio: retry because of signaling channel close reason ${e.reason}`);
                ctx.retryConnect();
            }
        };
        ws.onerror = error => {
            clearTimeout(timeout);
            err("audio: signaling channel error", error);
            reject(error);
        };
    });
}

function connectWebRtc(ctx) {
    dbg("audio: connecting WebRTC");
    return new Promise((resolve, reject) => {
        const pc = ctx.pc;
        let connectTimeout;
        let retrying = false;

        pc.ontrack = e => {
            if (ctx.settings.stateCb) ctx.settings.stateCb('track');
            e.receiver.playoutDelayHint = 0;
            document.getElementById(e.track.kind).srcObject = e.streams[0];
        }

        function handleError(error) {
            if (ctx.settings.disableRetry)
                reject(error);
            else if (!retrying) {
                retrying = true;
                err(`audio: connectWebRtc error ${error.message}, retry to connect`);
                pc.restartIce();
            }
        }

        pc.oniceconnectionstatechange = e => {
            dbg("audio: ICE connection state", pc.iceConnectionState);
            switch (pc.iceConnectionState) {
                case 'disconnected':
                case 'failed':
                    handleError(new Error('ICE connection failed'));
                    break;
            }
        }
        pc.onconnectionstatechange = e => {
            dbg("audio: Connection state", pc.connectionState);
            if (ctx.settings.stateCb) ctx.settings.stateCb(pc.connectionState);
            switch (pc.connectionState) {
                case 'connected':
                    if (connectTimeout) {
                        clearTimeout(connectTimeout);
                        connectTimeout = undefined;
                    }
                    resolve();
                    break;
                case 'disconnected':
                case 'failed':
                    handleError(new Error('connection failed'));
                    break;
            }
        };
        pc.onicecandidate = event => {
            if (event.candidate === null || !ctx.ws) return;
            dbg("audio: candidate", JSON.stringify(event.candidate));
            const message = { type: "candidate", data: JSON.stringify(event.candidate) };
            ctx.ws.send(JSON.stringify(message));
        };

        let negotiating = false;
        function negotiationneeded() {
            if (negotiating) return;
            negotiating = true;
            retrying = false;
            dbg("audio: WebRTC (re)negotiation")
            function sendOffer() {
                if (!ctx.ws) throw "signaling channel closed";
                dbg("audio: WebRTC offer", JSON.stringify(pc.localDescription));
                const message = { type: "sdp", data: JSON.stringify(pc.localDescription) };
                ctx.ws.send(JSON.stringify(message));
                negotiating = false;
            }
            function failed(error) {
                err("audio: negotiation failed", error);
                negotiating = false;
            }
            try {
                pc.createOffer().then(d => {
                    return pc.setLocalDescription(d).then(sendOffer);
                }).catch(failed);
            } catch (error) {
                err("audio: createOffer failed", error, "retry with deprecated version");
                pc.createOffer((d) => {
                    pc.setLocalDescription(d, sendOffer, failed);
                }, failed, { offerToReceiveAudio: true });
            }

            if (ctx.settings.connectTimeout) {
                connectTimeout = setTimeout(() => {
                    handleError(new Error("audio: connection timeout"));
                }, ctx.settings.connectTimeout);
            }
        }

        pc.onicegatheringstatechange = e => dbg("audio: ICE gathering state", pc.iceGatheringState)
        pc.onicecandidateerror = e => dbg("audio: ICE candidate error", e)
        pc.onnegotiationneeded = negotiationneeded;
        pc.onsignalingstatechange = e => dbg("audio: Signaling state", pc.signalingState)

        if (ctx.settings.unmuteCb)
          autoUnmute(ctx.settings);

        if (ctx.settings.enableDataChannel) {
            const dc = pc.createDataChannel("upstream", { ordered: false, maxRetransmits: 0 });
            dc.onopen = () => {
                window.n2iDataChannel = dc;
                dispatchEvent(new CustomEvent("n2i_pip3d_data_channel", { detail: dc }));
                console.info("upstream datachannel ready");
            };
            dc.onclose = () => {
                delete window.n2iDataChannel;
                dispatchEvent(new CustomEvent("n2i_pip3d_data_channel", { detail: null }));
                console.info("upstream datachannel closed");
            };
        }

        try {
            pc.addTransceiver('audio', {'direction': 'recvonly'})
        } catch (error) {
            err("audio: failed to add transceiver", error);
        }
        negotiationneeded();
    });
}

export function seek(pos) {
    dbg("audio: seek to", pos)
    const audio = document.getElementById('audio');
    if (!audio || !currentCtx || currentCtx.stopping) return;
    if (audio.paused)
        currentCtx.playTime = Date.now() - pos * 1000;
    else
        audio.currentTime = pos;
}

function _stop(ctx) {
    const audio = document.getElementById('audio');
    if (audio) {
        audio.pause();
        audio.currentTime = 0;
    }
    const pc = ctx.pc;
    ctx.retryConnect = undefined;
    if (ctx.ws) ctx.ws.close(1000, "stopping");
    if (ctx.abort) ctx.abort();
    if (pc) {
        // remove handlers synchronously
        pc.ontrack =
        pc.oniceconnectionstatechange =
        pc.onicecandidate =
        pc.onconnectionstatechange =
        pc.onicegatheringstatechange =
        pc.onicecandidateerror =
        pc.onnegotiationneeded =
        pc.onsignalingstatechange =
            undefined;
        return new Promise(resolve => {
            if (pc) pc.close();
            resolve();
        });
    } else
        return Promise.resolve();
}

export function stop2(ctx) {
    dbg("audio: disconnecting")
    ctx = ctx || currentCtx;
    if (!ctx || ctx.stopping) return;
    ctx.stopping = true;
    return _stop(ctx).then(function() {
        dbg("audio: disconnected")
    });
}

// legacy api
export function start(signalingUrl, conf, stateCb, unmuteCb, timeout) {
    const settings = {
        signalingUrl: signalingUrl,
        getWebRTCConf: () => { return Promise.resolve(conf) },
        stateCb: stateCb,
        unmuteCb: unmuteCb,
        connectTimeout: timeout,
    };
    start2(settings);
    return true;
}

export function stop(isRestart) {
    return stop2();
}
